// src/components/Toolbar/FileActions/FileActions.tsx
import React from 'react';
import { FolderOpen, Download, Sparkles } from 'lucide-react';
import { OpenButton } from './OpenButton';
import { SaveButton } from './SaveButton';
import { QuickExportButton } from './QuickExportButton';
import { NewDiagramButton } from './NewDiagramButton';
import { ExportDialog } from '@/components/Dialogs';
import { useUiStateStore } from '@stores/uiStateStore';
import { useModelStore } from '@stores/modelStore';

export const FileActions: React.FC = () => {
  // Use store state instead of local state
  const showExportDialog = useUiStateStore((state) => state.showExportDialog);
  const openExportDialog = useUiStateStore(
    (state) => state.actions.openExportDialog
  );
  const closeExportDialog = useUiStateStore(
    (state) => state.actions.closeExportDialog
  );

  // Check if canvas has content
  const items = useModelStore((state) => state.items);
  const hasCanvasContent = items.length > 0;

  return (
    <>
      <div className="flex items-center gap-3">
        {/* File Actions Group */}
        <div className="flex items-center gap-1.5">
          <NewDiagramButton />

          {/* Open */}
          <button
            onClick={() => {
              const openBtn = document.querySelector(
                '[data-open-button]'
              ) as HTMLButtonElement;
              openBtn?.click();
            }}
            className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50 hover:shadow dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-700"
            title="Open diagram (Ctrl+O)"
          >
            <FolderOpen className="h-4 w-4" />
            <span className="hidden md:inline">Open</span>
          </button>

          {/* Hidden actual OpenButton */}
          <div className="hidden">
            <OpenButton />
          </div>

          {/* Save */}
          <SaveButton />
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />

        {/* Export Actions Group */}
        <div className="flex items-center gap-1.5">
          {/* Standard Export */}
          <button
            onClick={() => {
              openExportDialog();
            }}
            disabled={!hasCanvasContent}
            className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50 hover:shadow disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-700"
            title={
              !hasCanvasContent
                ? 'Cannot export empty canvas'
                : 'Export with options'
            }
          >
            <Download className="h-4 w-4" />
            <span className="hidden lg:inline">Export</span>
          </button>

          {/* Quick Export - UPGRADED THEMED VERSION */}
          <div className="relative group z-50">
            <button
              onClick={() => {
                const quickExportBtn = document.querySelector(
                  '[data-quick-export]'
                ) as HTMLButtonElement;
                quickExportBtn?.click();
              }}
              disabled={!hasCanvasContent}
              className="relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 shadow-md transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/50 hover:scale-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 dark:from-blue-600 dark:via-purple-600 dark:to-pink-600 dark:shadow-blue-500/30"
              aria-label="Quick Export"
              title={
                !hasCanvasContent
                  ? 'Cannot export empty canvas'
                  : 'Quick Export (PNG, 2x quality)'
              }
            >
              {/* Animated shine effect */}
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full" />

              {/* Sparkle icon with pulsing effect */}
              <Sparkles
                className="relative h-4 w-4 text-white drop-shadow-md group-hover:rotate-12 transition-transform duration-300"
                fill="white"
                strokeWidth={2}
              />

              {/* Subtle glow */}
              <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>

            {/* Enhanced Tooltip */}
            <div className="pointer-events-none absolute left-1/2 top-full z-[100] mt-2 -translate-x-1/2 whitespace-nowrap opacity-0 transition-opacity delay-300 group-hover:opacity-100">
              <div className="relative rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 px-3 py-1.5 text-xs font-medium text-white shadow-xl border border-white/10 dark:from-gray-800 dark:to-gray-900">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-3 w-3" />
                  <span>Quick Export PNG</span>
                </div>
                <div className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-gradient-to-br from-gray-900 to-gray-800 border-l border-t border-white/10 dark:from-gray-800 dark:to-gray-900" />
              </div>
            </div>
          </div>

          {/* Hidden actual QuickExportButton */}
          <div className="hidden">
            <QuickExportButton />
          </div>
        </div>
      </div>

      {/* Export Dialog - Controlled by store */}
      <ExportDialog
        open={showExportDialog}
        onClose={() => {
          closeExportDialog();
        }}
      />
    </>
  );
};
